<div class="page-container partner-detail">
  @if (isLoading) {
    <div class="flex flex-col items-center justify-center py-20">
      <div class="spinner-large mb-4"></div>
      <p class="text-slate-500 font-medium">Cargando detalles del partner...</p>
    </div>
  } @else if (partner) {
    <div class="max-w-[1400px] mx-auto">
      <!-- Back button and Header -->
      <div class="mb-8">
        <button
          routerLink="/admin/partners"
          class="flex items-center gap-2 text-slate-500 hover:text-brand-red transition-colors mb-4 text-sm font-medium"
        >
          <fa-icon [icon]="faArrowLeft"></fa-icon>
          Volver al listado
        </button>

        <div class="flex flex-col xl:flex-row justify-between items-start xl:items-end gap-6">
          <div class="flex items-start gap-4">
            <div class="partner-avatar-large">
              {{ partner.company_name.charAt(0).toUpperCase() }}
            </div>
            <div class="min-w-0">
              <div class="flex items-center gap-3 mb-1">
                <span class="text-slate-400 text-sm font-medium">ID: #{{ partner.id }}</span>
                <div class="status-menu-wrapper relative inline-block" (click)="$event.stopPropagation()">
                  <button
                    type="button"
                    class="status-badge status-badge--trigger"
                    [ngClass]="partner.status === 'active' ? 'status-badge--active' : 'status-badge--inactive'"
                    (click)="toggleStatusMenu()"
                  >
                    {{ partner.status === 'active' ? 'Activo' : 'No Activo' }}
                    <fa-icon [icon]="faCaretDown" class="text-[9px] ml-1"></fa-icon>
                  </button>

                  @if (showStatusMenu) {
                    <div class="status-dropdown-menu">
                      @for (opt of statusOptions; track opt.value) {
                        <button
                          type="button"
                          class="status-dropdown-option"
                          [class.is-active]="partner.status === opt.value"
                          [disabled]="isUpdatingStatus"
                          (click)="changeStatus(opt.value)"
                        >
                          {{ opt.label }}
                          @if (partner.status === opt.value) {
                            <fa-icon [icon]="faCheck" class="text-[10px]"></fa-icon>
                          }
                        </button>
                      }
                    </div>
                  }
                </div>
              </div>
              <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                {{ partner.company_name }}
              </h1>
              <div
                class="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-4 mt-2 text-slate-500 text-sm"
              >
                <div class="flex items-start gap-1.5">
                  <fa-icon [icon]="faCalendarAlt" class="text-brand-red/60 mt-0.5"></fa-icon>
                  <span>Registrado el {{ formatDate(partner.company_createdate) }}</span>
                </div>
                @if (partner.last_login_at) {
                  <div class="flex items-start gap-1.5">
                    <fa-icon [icon]="faClock" class="text-brand-red/60 mt-0.5"></fa-icon>
                    <span>Último acceso: {{ partner.last_login_at | date: 'dd/MM/yyyy HH:mm' }}</span>
                  </div>
                }
              </div>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-3 w-full xl:w-auto">
            <button (click)="openFilesModal()" class="app-btn app-btn--secondary">
              <fa-icon [icon]="faFile"></fa-icon>
              Documentos
            </button>
            <button (click)="openEditModal()" class="app-btn app-btn--primary">
              <fa-icon [icon]="faPen"></fa-icon>
              Editar Partner
            </button>
          </div>
        </div>

        @if (partner.status !== 'active') {
          <div class="alert-danger">
            <fa-icon [icon]="faTriangleExclamation"></fa-icon>
            <p>
              Este partner no se encuentra activo actualmente. Algunas
              funcionalidades para el partner pueden estar restringidas.
            </p>
            <button
              type="button"
              class="alert-danger__action"
              [disabled]="isUpdatingStatus"
              (click)="changeStatus('active')"
            >
              <fa-icon [icon]="faRotateLeft"></fa-icon>
              Reactivar partner
            </button>
          </div>
        }
      </div>

      <div class="grid grid-cols-1 min-[1500px]:grid-cols-4 gap-6 min-[1500px]:gap-8">
        <!-- Main Stats & Commissions -->
        <div class="min-[1500px]:col-span-3 space-y-6 lg:space-y-8">
          <!-- Quick Stats -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="stat-card">
              <div class="stat-icon-wrapper blue">
                <fa-icon [icon]="faBriefcase"></fa-icon>
              </div>
              <div class="stat-content">
                <span class="stat-label">Oportunidades</span>
                <span class="stat-value">{{ partner.opportunities_count || 0 }}</span>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon-wrapper purple">
                <fa-icon [icon]="faUsers"></fa-icon>
              </div>
              <div class="stat-content">
                <span class="stat-label">Usuarios</span>
                <span class="stat-value">{{ partner.users_count || 0 }}</span>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon-wrapper amber">
                <fa-icon [icon]="faTags"></fa-icon>
              </div>
              <div class="stat-content">
                <span class="stat-label">Tipo de Partner</span>
                <span class="stat-value stat-value--text">{{ partner.type || 'No definido' }}</span>
              </div>
            </div>
          </div>

          <div class="alert-info">
            <fa-icon [icon]="faCircleInfo"></fa-icon>
            <p>
              Las condiciones mostradas son las vigentes para este partner y prevalecerán sobre
              cualquier condición genérica recogida en el contrato, salvo pacto expreso entre las
              partes.
            </p>
          </div>

          <!-- Classification & Commercial Conditions -->
          <div class="grid grid-cols-1 min-[1200px]:grid-cols-3 gap-6 min-[1200px]:gap-8">
            <!-- Condiciones Generales -->
            <div class="detail-card overflow-hidden">
              <h3 class="card-title">
                <fa-icon [icon]="faTags" class="mr-1.5 text-brand-red/70"></fa-icon>
                Condiciones Generales
              </h3>
              <div class="space-y-1">
                <span class="label">Modelo de Relación</span>
                <span class="value text-slate-900 dark:text-white block">{{
                  partner.relationship_type || 'No definido'
                }}</span>
                @if (partner.relationship_type && relationshipTypeDescriptions[partner.relationship_type]) {
                  <p class="text-xs text-slate-400 mt-1 leading-relaxed">
                    {{ relationshipTypeDescriptions[partner.relationship_type] }}
                  </p>
                }
              </div>

              <hr class="section-divider" />

              <div class="space-y-4">
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faGaugeHigh"></fa-icon></div>
                  <div class="info-data flex-1">
                    <span class="label">Límite por Operación</span>
                    <span class="value text-slate-900 dark:text-white"
                      >{{
                        partner.operation_limit !== null && partner.operation_limit !== undefined
                          ? formatCurrency(partner.operation_limit)
                          : '-'
                      }}
                      @if (partner.operation_limit !== null && partner.operation_limit !== undefined) {
                        <span class="hint">(Sin IVA)</span>
                      }
                    </span>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faCalendarDays"></fa-icon></div>
                  <div class="info-data flex-1">
                    <span class="label">Periodicidad Liquidación</span>
                    <span class="value text-slate-900 dark:text-white">{{
                      partner.settlement_periodicity || '-'
                    }}</span>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faUserPlus"></fa-icon></div>
                  <div class="info-data flex-1">
                    <span class="label">Mínimo Nuevos Clientes / Año</span>
                    <span class="value text-slate-900 dark:text-white">{{
                      partner.min_new_clients_per_year ?? '-'
                    }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Condiciones Comerciales -->
            <div class="detail-card overflow-hidden min-[1200px]:col-span-2">
              <h3 class="card-title">
                <fa-icon [icon]="faCommentDots" class="mr-1.5 text-brand-red/70"></fa-icon>
                Condiciones Comerciales
              </h3>
              <div
                class="grid grid-cols-1 gap-6"
                [class.sm:grid-cols-2]="partner.relationship_type === 'Distribuidor'"
              >
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faPercent"></fa-icon></div>
                  <div class="info-data">
                    <span class="label">Comisión Fija</span>
                    <span class="value text-slate-900 dark:text-white">{{
                      partner.fixed_commission !== null && partner.fixed_commission !== undefined
                        ? partner.fixed_commission + '%'
                        : 'Por producto'
                    }}</span>
                    <span class="hint">Sobre proyectos e implantaciones.</span>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faArrowsRotate"></fa-icon></div>
                  <div class="info-data">
                    <span class="label">Comisión Recurrente</span>
                    <span class="value text-slate-900 dark:text-white"
                      >{{ partner.recurring_commission ?? 0 }}%</span
                    >
                    <span class="hint">Sobre servicios recurrentes según condiciones vigentes.</span>
                  </div>
                </div>
              </div>

              @if (partner.relationship_type === 'Distribuidor') {
                <hr class="section-divider" />

                <div class="distribution-group">
                  <div class="space-y-1">
                    <span class="label">Modelo de Distribución</span>
                    <span class="value text-slate-900 dark:text-white block">{{
                      partner.distribution_modality || 'No definido'
                    }}</span>
                    @if (partner.distribution_modality && distributionModalityDescriptions[partner.distribution_modality]) {
                      <p class="text-xs text-slate-400 mt-1 leading-relaxed">
                        {{ distributionModalityDescriptions[partner.distribution_modality] }}
                      </p>
                    }
                  </div>

                  @if (partner.distribution_modality && distributionModalityConditions[partner.distribution_modality]) {
                    <p class="distribution-group__caption">
                      <fa-icon [icon]="faArrowRight"></fa-icon>
                      Condiciones que aplican con esta modalidad
                    </p>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div class="info-row">
                        <div class="info-icon"><fa-icon [icon]="faFileInvoiceDollar"></fa-icon></div>
                        <div class="info-data">
                          <span class="label">Contratación y Facturación</span>
                          <span class="value text-slate-900 dark:text-white">{{
                            distributionModalityConditions[partner.distribution_modality].contracting
                          }}</span>
                        </div>
                      </div>
                      <div class="info-row">
                        <div class="info-icon"><fa-icon [icon]="faScrewdriverWrench"></fa-icon></div>
                        <div class="info-data">
                          <span class="label">Implantación</span>
                          <span class="value text-slate-900 dark:text-white">{{
                            distributionModalityConditions[partner.distribution_modality].implementation
                          }}</span>
                        </div>
                      </div>
                      <div class="info-row">
                        <div class="info-icon"><fa-icon [icon]="faHeadset"></fa-icon></div>
                        <div class="info-data">
                          <span class="label">Soporte</span>
                          <span class="value text-slate-900 dark:text-white">{{
                            distributionModalityConditions[partner.distribution_modality].support
                          }}</span>
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>

          <!-- Partner Comments -->
          @if (partner.comments) {
            <div class="detail-card detail-card--sm">
              <h3 class="card-title">
                <fa-icon [icon]="faCommentDots" class="mr-1.5 text-brand-red/70"></fa-icon>
                Comentarios del Partner
              </h3>
              <div class="comments-box">
                <p>{{ partner.comments }}</p>
              </div>
            </div>
          }

          <!-- Tabs or More Sections -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <!-- Contact Details -->
            <div class="detail-card">
              <h3 class="card-title">
                Datos de Contacto
                <fa-icon
                  [icon]="faLock"
                  class="ml-1 text-xs text-gray-400"
                  title="Campos de solo lectura"
                ></fa-icon>
              </h3>
              <div class="space-y-4">
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faEnvelope"></fa-icon></div>
                  <div class="info-data">
                    <span class="label">Email Corporativo</span>
                    <span class="value text-slate-900 dark:text-white">{{
                      partner.company_email || 'No disponible'
                    }}</span>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faPhone"></fa-icon></div>
                  <div class="info-data">
                    <span class="label">Teléfono</span>
                    <span class="value text-slate-900 dark:text-white">{{
                      partner.company_phone || 'No disponible'
                    }}</span>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faMapMarkerAlt"></fa-icon></div>
                  <div class="info-data">
                    <span class="label">Dirección</span>
                    <span class="value text-slate-900 dark:text-white">{{
                      partner.company_address || 'No disponible'
                    }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Administrative Details -->
            <div class="detail-card">
              <h3 class="card-title">
                Datos Administrativos
                <fa-icon
                  [icon]="faLock"
                  class="ml-1 text-xs text-gray-400"
                  title="Campos de solo lectura"
                ></fa-icon>
              </h3>
              <div class="space-y-4">
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faIdCard"></fa-icon></div>
                  <div class="info-data">
                    <span class="label">CIF</span>
                    <span class="value font-mono text-sm">{{
                      partner.cif || 'No definido'
                    }}</span>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faCreditCard"></fa-icon></div>
                  <div class="info-data">
                    <span class="label">IBAN de Empresa</span>
                    <span class="value font-mono text-sm">{{
                      partner.company_iban || 'No definido'
                    }}</span>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faBriefcase"></fa-icon></div>
                  <div class="info-data">
                    <span class="label">ID HubSpot</span>
                    <span class="value">{{ partner.company_id }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar / Secondary Info -->
        <div class="min-[1500px]:col-span-1 space-y-6 lg:space-y-8">
          <!-- Contract Section -->
          @if (partner.relationship_type && partner.contract) {
            <div class="detail-card">
              <h3 class="card-title">
                <fa-icon [icon]="faFileContract" class="mr-1.5 text-brand-red/70"></fa-icon>
                Contrato
              </h3>

              <div class="space-y-4">
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faFileContract"></fa-icon></div>
                  <div class="info-data">
                    <span class="label">Nº de Contrato</span>
                    <span class="value text-slate-900 dark:text-white">{{ partner.contract.number }}</span>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faCircleInfo"></fa-icon></div>
                  <div class="info-data">
                    <span class="label">Estado</span>
                    <span
                      class="status-badge"
                      [ngClass]="partner.contract.status === 'signed' ? 'status-badge--signed' : 'status-badge--pending'"
                    >
                      {{ partner.contract.status === 'signed' ? 'Firmado' : 'Pendiente de firma' }}
                    </span>
                  </div>
                </div>

                @if (partner.contract.status === 'signed') {
                  <div class="info-row">
                    <div class="info-icon"><fa-icon [icon]="faCalendarDays"></fa-icon></div>
                    <div class="info-data">
                      <span class="label">Fecha de Firma</span>
                      <span class="value text-slate-900 dark:text-white">{{ formatDate(partner.contract.signed_at) }}</span>
                    </div>
                  </div>

                  <div class="info-row">
                    <div class="info-icon"><fa-icon [icon]="faClockRotateLeft"></fa-icon></div>
                    <div class="info-data">
                      <span class="label">Fecha de Renovación</span>
                      <span class="value text-slate-900 dark:text-white">{{ formatDate(partner.contract.renewal_date) }}</span>
                    </div>
                  </div>
                }

                @if (partner.contract.document_name) {
                  <div class="info-row">
                    <div class="info-icon"><fa-icon [icon]="faFile"></fa-icon></div>
                    <div class="info-data">
                      <span class="label">Documento</span>
                      <span class="value text-slate-900 dark:text-white flex items-center gap-2">
                        {{ partner.contract.document_name }}
                        <button
                          (click)="downloadContract(partner.contract.number, partner.contract.document_name)"
                          class="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-all"
                          title="Descargar contrato"
                        >
                          <fa-icon [icon]="faDownload" class="text-xs"></fa-icon>
                        </button>
                      </span>
                    </div>
                  </div>
                }
              </div>
            </div>
          }

          <!-- Documents List -->
          <div class="detail-card">
            <h3 class="card-title">Documentos</h3>

            <div class="space-y-3">
              @if (isFilesLoading) {
                <div class="flex flex-col items-center py-6 opacity-50">
                  <div class="w-6 h-6 border-2 border-brand-red border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span class="text-[10px]">Cargando...</span>
                </div>
              } @else if (files.length === 0) {
                <div class="text-center py-8 bg-slate-50 dark:bg-slate-900/40 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                  <fa-icon [icon]="faFile" class="text-2xl text-slate-200 dark:text-slate-700 mb-2"></fa-icon>
                  <p class="text-[10px] text-slate-400 font-medium">No hay documentos subidos.</p>
                </div>
              } @else {
                @for (file of files; track file.id) {
                  <div class="flex items-center gap-3 p-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg hover:border-brand-red/30 transition-all group">
                    <div class="w-8 h-8 rounded bg-gray-50 dark:bg-gray-700 flex items-center justify-center text-gray-400 group-hover:text-brand-red transition-colors text-xs">
                      <fa-icon [icon]="faFile"></fa-icon>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-xs font-semibold text-gray-900 dark:text-white truncate" [title]="file.name">{{ file.name }}</p>
                      <span class="text-[9px] text-gray-500">{{ formatBytes(file.size) }}</span>
                    </div>
                    <button (click)="downloadFile(file)" class="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-all" title="Descargar">
                      <fa-icon [icon]="faDownload" class="text-xs"></fa-icon>
                    </button>
                  </div>
                }
              }
            </div>
          </div>

          <!-- Recent Activity -->
          <div class="detail-card">
            <h4 class="text-slate-600 dark:text-slate-400 font-semibold mb-4 text-sm flex items-center gap-2">
              <fa-icon [icon]="faClockRotateLeft"></fa-icon>
              Actividad Reciente
            </h4>

            @if (isConditionLogsLoading && conditionLogs.length === 0) {
              <div class="flex flex-col items-center justify-center py-8 gap-3">
                <div class="w-8 h-8 border-4 border-brand-red/20 border-t-brand-red rounded-full animate-spin"></div>
                <p class="text-slate-400 text-[10px]">Cargando actividad...</p>
              </div>
            } @else if (conditionLogsError) {
              <div class="text-center py-8 bg-slate-50 dark:bg-slate-900/40 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                <p class="text-[10px] text-slate-400 font-medium mb-2">No se pudo cargar la actividad.</p>
                <button (click)="loadConditionLogs(partner.id)" class="text-[10px] font-semibold text-brand-red hover:underline">
                  Reintentar
                </button>
              </div>
            } @else if (conditionLogs.length === 0) {
              <div class="text-center py-8 bg-slate-50 dark:bg-slate-900/40 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                <fa-icon [icon]="faClock" class="text-2xl text-slate-200 dark:text-slate-700 mb-2"></fa-icon>
                <p class="text-[10px] text-slate-400 font-medium">Sin actividad reciente.</p>
              </div>
            } @else {
              <div class="activity-timeline-scroll">
                <div class="space-y-4 relative before:absolute before:inset-0 before:ml-3.5 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent dark:before:via-slate-800">
                  @for (log of conditionLogs; track log.id) {
                    <div class="relative flex items-start gap-3">
                      <div class="flex-shrink-0 w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 flex items-center justify-center z-10 text-[10px]">
                        <fa-icon [icon]="faPen"></fa-icon>
                      </div>
                      <div class="flex-1 min-w-0 bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 p-3 rounded-xl">
                        <div class="flex items-center justify-between gap-2 mb-1.5">
                          <span class="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                            {{ conditionLogFieldLabel(log.field) }}
                          </span>
                          <time class="text-[9px] text-gray-400 flex items-center gap-1 whitespace-nowrap">
                            <fa-icon [icon]="faClock"></fa-icon>
                            {{ log.created_at | date: 'dd/MM/yyyy HH:mm' }}
                          </time>
                        </div>
                        <p class="text-xs text-gray-700 dark:text-gray-300">
                          <span class="line-through text-gray-400">{{ log.old_value ?? '-' }}</span>
                          <fa-icon [icon]="faArrowRight" class="text-[9px] mx-1.5 text-gray-300"></fa-icon>
                          <span class="font-semibold text-gray-900 dark:text-white">{{ log.new_value ?? '-' }}</span>
                        </p>
                        <p class="text-[9px] text-gray-400 mt-1.5">
                          @if (log.source === 'automatic' || !log.user) {
                            Cambio automático por modelo
                          } @else {
                            Por {{ log.user.name }} ({{ log.user.email }})
                          }
                        </p>
                      </div>
                    </div>
                  }
                </div>
              </div>

              @if (conditionLogsPage < conditionLogsLastPage) {
                <div class="text-center mt-4">
                  <button
                    (click)="loadMoreConditionLogs()"
                    [disabled]="isConditionLogsLoading"
                    class="text-[10px] font-semibold text-brand-red hover:underline disabled:opacity-50"
                  >
                    {{ isConditionLogsLoading ? 'Cargando...' : 'Ver más' }}
                  </button>
                </div>
              }
            }
          </div>
        </div>
      </div>
    </div>
  } @else {
    <div class="flex flex-col items-center justify-center py-20 text-center">
      <div class="text-slate-300 text-6xl mb-4">
        <fa-icon [icon]="faCircleInfo"></fa-icon>
      </div>
      <h3 class="text-xl font-bold text-slate-900 dark:text-white">Partner no encontrado</h3>
      <button routerLink="/admin/partners" class="btn-cancel mt-4">Volver al listado</button>
    </div>
  }
</div>

<!-- Edit Modal -->
@if (showEditModal && partner) {
  <app-edit-partner-modal
    [partner]="partner"
    (close)="closeEditModal()"
    (success)="onEditSuccess($event)"
  ></app-edit-partner-modal>
}

@if (showFilesModal && partner) {
  <app-partner-files-modal
    [partner]="partner"
    (close)="closeFilesModal()"
  ></app-partner-files-modal>
}
